<template>
  <div id="resultados-page">
    <Breadcrumb :items="breadcrumbItems" />

    <main class="search-layout">
      <header class="page-heading">
        <div>
          <h1>Resultados da pesquisa</h1>
          <p v-if="termoBusca">
            Resultados para <strong>“{{ termoBusca }}”</strong>
          </p>
          <p v-else>Pesquise por ações, atividades, notícias, vagas e parcerias.</p>
        </div>
      </header>

      <nav class="search-sidebar" aria-label="Filtrar resultados por categoria">
        <span class="sidebar-title">Filtrar por</span>

        <button v-for="filtro in filtros" :key="filtro.valor" type="button" class="filter-item"
          :class="{ active: filtroAtivo === filtro.valor }" :aria-pressed="filtroAtivo === filtro.valor"
          @click="mudarFiltro(filtro.valor)">
          <span class="filter-label">
            <i :class="filtro.icone" aria-hidden="true"></i>
            <span>{{ filtro.label }}</span>
          </span>

          <span class="filter-counter">
            {{ estatisticas[filtro.valor] || 0 }}
          </span>
        </button>
      </nav>

      <section class="search-results-content" aria-label="Resultados encontrados">
        <div class="results-toolbar">
          <h2>
            <template v-if="isLoading">Buscando resultados...</template>
            <template v-else>
              {{ estatisticas[filtroAtivo] || 0 }}
              {{ (estatisticas[filtroAtivo] || 0) === 1
                ? 'resultado encontrado'
                : 'resultados encontrados' }}
            </template>
          </h2>

          <span v-if="!isLoading && resultados.length" class="results-page-info">
            Página {{ currentPage + 1 }} de {{ totalPages }}
          </span>
        </div>

        <div v-if="isLoading" class="loading-state" role="status">
          <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
          <span>Carregando resultados...</span>
        </div>

        <div v-else-if="erroBusca" class="empty-state error-state" role="alert">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <h3>Não foi possível carregar os resultados</h3>
          <p>Tente novamente em alguns instantes.</p>
          <button type="button" class="retry-button" @click="carregarResultados">
            Tentar novamente
          </button>
        </div>

        <div v-else-if="!termoBusca.trim()" class="empty-state">
          <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
          <h3>O que você está procurando?</h3>
          <p>Digite um termo na barra de pesquisa para encontrar resultados.</p>
        </div>

        <div v-else-if="resultados.length === 0" class="empty-state">
          <i class="fa-regular fa-folder-open" aria-hidden="true"></i>
          <h3>Nenhum resultado encontrado</h3>
          <p>
            Não encontramos resultados para
            <strong>“{{ termoBusca }}”</strong>.
          </p>
          <p class="empty-hint">
            Tente utilizar outras palavras ou selecionar outra categoria.
          </p>
        </div>

        <div v-else class="results-list">
          <article v-for="item in resultados" :key="`${item.type}-${item.id}`" class="result-card" role="link"
            tabindex="0" :aria-label="`Abrir ${item.title}`" @click="abrirResultado(item)"
            @keydown.enter.prevent="abrirResultado(item)" @keydown.space.prevent="abrirResultado(item)">
            <div class="result-container">
              <div class="result-title-wrapper">
                <h3 class="result-card-title" v-html="destacarTermo(item.title)"></h3>

                <span class="type-badge">
                  {{ formatarTipo(item.type) }}
                </span>
              </div>

              <p v-if="item.description" class="result-card-description" v-html="destacarTermo(item.description)"></p>

              <span class="result-link">
                Ver detalhes
                <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
              </span>
            </div>
          </article>
        </div>

        <Paginacao v-if="!isLoading && !erroBusca && resultados.length" :total-paginas="totalPages"
          :pagina-atual="currentPage" @mudar-pagina="mudarPagina" />
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import buscaGeralService from "@/service/buscaGeralService";
import Breadcrumb from "@/components/common/Breadcrumb.vue";
import Paginacao from "@/components/common/paginacaoComponent.vue";

const route = useRoute();
const router = useRouter();

const termoBusca = ref(String(route.query.q || ""));
const filtroAtivo = ref(String(route.query.filtro || "GERAL"));
const resultados = ref([]);
const estatisticas = ref({});
const isLoading = ref(false);
const erroBusca = ref(false);

const currentPage = ref(0);
const totalPages = ref(1);

let requisicaoAtual = 0;

const filtros = [
  { valor: "GERAL", label: "Geral", icone: "fa-solid fa-globe" },
  { valor: "ATIVIDADE", label: "Atividades", icone: "fa-solid fa-calendar-days" },
  { valor: "ACAO", label: "Ações", icone: "fa-solid fa-briefcase" },
  { valor: "VAGA", label: "Vagas", icone: "fa-solid fa-user-graduate" },
  { valor: "NOTICIA", label: "Notícias", icone: "fa-solid fa-newspaper" },
  { valor: "PARCERIA", label: "Parcerias", icone: "fa-solid fa-handshake" },
];

const breadcrumbItems = computed(() => [
  { label: "Início", to: "/" },
  { label: "Resultados", to: "/resultados" },
]);

onMounted(() => {
  carregarResultados();
});

watch(
  () => [route.query.q, route.query.filtro],
  ([novoTermo, novoFiltro]) => {
    termoBusca.value = String(novoTermo || "");
    filtroAtivo.value = filtros.some((f) => f.valor === novoFiltro)
      ? String(novoFiltro)
      : "GERAL";

    currentPage.value = 0;
    carregarResultados();
  }
);

async function carregarResultados() {
  const requisicao = ++requisicaoAtual;
  const termo = termoBusca.value.trim();

  if (!termo) {
    resultados.value = [];
    estatisticas.value = {};
    totalPages.value = 1;
    currentPage.value = 0;
    erroBusca.value = false;
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  erroBusca.value = false;

  try {
    const dados = await buscaGeralService.buscaGeral(
      termo,
      filtroAtivo.value,
      currentPage.value,
      6
    );

    // Ignora respostas antigas caso uma busca mais recente já tenha começado.
    if (requisicao !== requisicaoAtual) return;

    const itens = dados.items || [];
    const chaves = new Set();

    resultados.value = itens.filter((item) => {
      const chave = `${item.type}-${item.id}`;

      if (chaves.has(chave)) return false;

      chaves.add(chave);
      return true;
    });

    estatisticas.value = dados.stats || {};
    currentPage.value = dados.currentPage ?? currentPage.value;
    totalPages.value = Math.max(1, dados.totalPages ?? 1);
  } catch (error) {
    if (requisicao !== requisicaoAtual) return;

    console.error("Erro ao carregar resultados da busca:", error);

    resultados.value = [];
    totalPages.value = 1;
    erroBusca.value = true;
  } finally {
    if (requisicao === requisicaoAtual) {
      isLoading.value = false;
    }
  }
}

function mudarFiltro(novoFiltro) {
  if (filtroAtivo.value === novoFiltro) return;

  router.replace({
    query: {
      ...route.query,
      filtro: novoFiltro,
      page: undefined,
    },
  });
}

async function mudarPagina(novaPagina) {
  if (
    novaPagina < 0 ||
    novaPagina >= totalPages.value ||
    novaPagina === currentPage.value ||
    isLoading.value
  ) {
    return;
  }

  currentPage.value = novaPagina;
  await carregarResultados();
}

function formatarTipo(tipoBd) {
  const mapa = {
    ACAO: "Ação de Extensão",
    ATIVIDADE: "Atividade",
    PARCERIA: "Parceria",
    NOTICIA: "Notícia",
    VAGA: "Vaga",
    GERAL: "Geral",
  };

  return mapa[tipoBd] || tipoBd;
}


function destacarTermo(textoOriginal) {
  if (!textoOriginal) return "";

  let texto = String(textoOriginal).trim();

  // Remove aspas externas quando a descrição está serializada como JSON.
  if (texto.startsWith('"') && texto.endsWith('"')) {
    try {
      const textoDecodificado = JSON.parse(texto);

      if (typeof textoDecodificado === "string") {
        texto = textoDecodificado;
      }
    } catch {
      texto = texto.slice(1, -1);
    }
  }

  // Converte HTML em texto puro, removendo tags da descrição.
  const elemento = document.createElement("div");
  elemento.innerHTML = texto;
  texto = elemento.textContent || "";

  const termo = termoBusca.value.trim();

  // Escapa caracteres especiais para evitar inserir HTML indevido.
  const textoSeguro = texto.replace(/[&<>"']/g, (caractere) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[caractere]);

  if (!termo) return textoSeguro;

  const termoSeguro = termo.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${termoSeguro})`, "gi");

  return textoSeguro.replace(regex, "<mark>$1</mark>");
}

function abrirResultado(item) {
  const rotasPorTipo = {
    ACAO: `/acao/${item.id}/informacoes`,
    ATIVIDADE: `/atividades/${item.id}`,
    NOTICIA: `/home-noticia/${item.id}`,
    PARCERIA: `/parcerias/${item.id}`,
    VAGA: `/vaga/${item.id}`,
  };

  const rota = rotasPorTipo[item.type];

  if (!rota) {
    s
    console.warn("Tipo de resultado não mapeado:", item.type);
    return;
  }

  router.push(rota);
}
</script>

<style scoped>
#resultados-page {
  min-height: calc(100vh - 130px);
  color: var(--cinza-escuro);
}

.search-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 32px;
}

.page-heading {
  padding: 12px 0 20px;
  border-bottom: 1px solid var(--cinza-claro);
}


.page-heading h1 {
  margin: 8px 0;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
}

.page-heading p {
  margin: 0;
  color: var(--cinza-escuro);
  line-height: 1.6;
}

.search-sidebar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.sidebar-title {
  margin-right: 8px;
  font-size: 14px;
  font-weight: 600;
}

.filter-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid var(--cinza-claro);
  border-radius: 8px;
  background: var(--branco);
  color: var(--cinza-escuro);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.filter-item:hover {
  border-color: var(--roxo-primario);
}

.filter-item.active {
  border-color: var(--roxo-primario);
  background: var(--roxo-superficie);
  color: var(--roxo-primario);
  font-weight: 600;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label i {
  width: 16px;
  text-align: center;
}

.filter-counter {
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 20px;
  background: var(--cinza-claro);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
}

.filter-item.active .filter-counter {
  background: var(--branco);
  color: var(--roxo-primario);
}

.search-results-content {
  min-width: 0;
}

.results-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--cinza-claro);
}

.results-toolbar h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.results-page-info {
  color: var(--cinza-escuro);
  font-size: 13px;
  white-space: nowrap;
}

.results-list {
  display: flex;
  flex-direction: column;
}

.result-card {
  display: flex;
  padding: 20px 16px;
  border-bottom: 1px solid var(--cinza-claro);
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.result-card:hover {
  background: var(--cinza-claro);
}

.result-card:focus-visible {
  outline: 2px solid var(--roxo-primario);
  outline-offset: -2px;
}

.result-container {
  width: 100%;
  min-width: 0;
}

.result-title-wrapper {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 8px;
}

.result-card-title {
  min-width: 0;
  margin: 0;
  color: var(--roxo-primario);
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.result-card:hover .result-card-title {
  text-decoration: underline;
}

.type-badge {
  flex-shrink: 0;
  padding: 4px 9px;
  border-radius: 20px;
  background: var(--roxo-superficie);
  color: var(--roxo-primario);
  font-size: 11px;
  font-weight: 600;
}

.result-card-description {
  display: -webkit-box;
  margin: 0 0 12px;
  overflow: hidden;
  color: var(--cinza-escuro);
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.result-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--roxo-primario);
  font-size: 13px;
  font-weight: 600;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 56px 20px;
  text-align: center;
}

.loading-state {
  flex-direction: row;
  color: var(--roxo-primario);
}

.loading-state i,
.empty-state>i {
  font-size: 28px;
}

.empty-state h3,
.empty-state p {
  margin: 0;
}

.empty-state h3 {
  font-size: 18px;
}

.empty-state p {
  max-width: 480px;
  font-size: 14px;
  line-height: 1.6;
}

.empty-hint {
  opacity: 0.8;
}

.error-state>i {
  color: var(--roxo-primario);
}

.retry-button {
  margin-top: 8px;
  padding: 10px 16px;
  border: 0;
  border-radius: 6px;
  background: var(--roxo-primario);
  color: var(--branco);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.retry-button:hover {
  background: var(--roxo-claro);
}

:deep(mark) {
  padding: 0 2px;
  border-radius: 2px;
  background: var(--laranja-claro);
  color: var(--cinza-escuro);
  font-weight: 700;
}

@media (max-width: 768px) {
  .search-layout {
    gap: 18px;
  }

  .search-sidebar {
    gap: 8px;
  }

  .sidebar-title {
    width: 100%;
    margin-bottom: 4px;
  }

  .filter-item {
    flex: 1 1 auto;
  }

  .results-toolbar h2 {
    font-size: 16px;
  }

  .result-card {
    padding: 18px 8px;
  }
}

@media (max-width: 480px) {
  .page-heading {
    padding-bottom: 16px;
  }

  .filter-item {
    min-height: 38px;
    padding: 7px 9px;
    font-size: 12px;
  }

  .results-toolbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .result-title-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }

  .result-card-title {
    font-size: 16px;
  }

  .loading-state,
  .empty-state {
    padding: 40px 12px;
  }
}
</style>
